import { useEffect, useState } from 'react';
import { AlertCircle, Calendar, CheckCircle, MessageSquare, Paperclip, Plus, Send, Settings, Trash2, X } from 'lucide-react';
import { Modal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/common/StatusBadge';
import { UserSelect } from '../../components/common/UserSelect';
import { FileDownloadLink } from '../../components/common/FileDownloadLink';
import { MentionInput } from '../../components/common/MentionInput';
import { formatDate } from '../../utils/format';
import { useProfiles } from '../../contexts/ProfilesContext';
import { supabase } from '../../lib/supabase';
import { uploadFiles } from '../../lib/storage';
import { stripTags } from '../../utils/sanitize';
import { useToast } from '../../contexts/ToastContext';

export const LATAMProjectsPage = ({ user, projects, setProjects, notifications, setNotifications }) => {
  const { profiles, getUserName } = useProfiles();
  const { showToast } = useToast();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newProject, setNewProject] = useState({ name: '', description: '', country: 'Mexico', partner: '' });
  const [newSubtask, setNewSubtask] = useState({ title: '', comment: '', assignedTo: '', dueDate: '' });
  const [subtaskFiles, setSubtaskFiles] = useState([]);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [approvalModal, setApprovalModal] = useState(null);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [commentFiles, setCommentFiles] = useState([]);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [highlightSubtaskId, setHighlightSubtaskId] = useState(null);

  // Deep-link: if Home stashed a task target, open that project and highlight the task.
  useEffect(() => {
    const raw = localStorage.getItem('luye_open_task');
    if (!raw || projects.length === 0) return;
    try {
      const { projectId, subtaskId } = JSON.parse(raw);
      const project = projects.find(p => p.id === projectId);
      if (project) { setSelectedProject(project); setHighlightSubtaskId(subtaskId); }
    } catch { /* ignore malformed target */ }
    localStorage.removeItem('luye_open_task');
  }, [projects]);

  // Scroll the highlighted task into view once its project panel is open.
  useEffect(() => {
    if (!selectedProject || !highlightSubtaskId) return;
    const t = setTimeout(() => {
      document.getElementById(`subtask-${highlightSubtaskId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    const clear = setTimeout(() => setHighlightSubtaskId(null), 3000);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [selectedProject, highlightSubtaskId]);

  const countries = ['Mexico', 'Brazil', 'Argentina', 'Colombia', 'Chile', 'Uruguay', 'CAMCAR'];
  const partners = ['M8 Mexico', 'M8 Brazil', 'Libra Chile', 'Libra Uruguay', 'Bagó'];

  const createNotification = async (type, message, toEmail) => {
    const notif = { id: `n_${Date.now()}`, type, message, to: toEmail, read: false, createdAt: new Date().toISOString() };
    setNotifications(prev => [notif, ...prev]);
    await supabase.from('notifications').insert({ type, message, to_email: toEmail });
  };

  const createProject = async () => {
    if (!newProject.name) return;
    const { data, error } = await supabase.from('latam_projects').insert({
      name: stripTags(newProject.name), description: stripTags(newProject.description || ''), country: newProject.country, partner: stripTags(newProject.partner || ''), created_by: user.email
    }).select().single();
    if (error) { showToast('Failed to create project'); return; }
    setProjects([...projects, { id: data.id, name: data.name, description: data.description, country: data.country, partner: data.partner, createdBy: data.created_by, createdAt: data.created_at, subtasks: [], comments: [] }]);
    setNewProject({ name: '', description: '', country: 'Mexico', partner: '' });
    setShowProjectModal(false);
  };

  const addSubtask = async () => {
    if (!newSubtask.title || !newSubtask.assignedTo || !newSubtask.dueDate) return;
    const { data, error } = await supabase.from('latam_subtasks').insert({
      project_id: selectedProject.id, title: stripTags(newSubtask.title), assigned_to: newSubtask.assignedTo, due_date: newSubtask.dueDate, comment: stripTags(newSubtask.comment || ''), files: subtaskFiles, status: 'pending'
    }).select().single();
    if (error) { showToast('Failed to add subtask'); return; }
    const subtask = { id: data.id, title: data.title, assignedTo: data.assigned_to, dueDate: data.due_date, comment: data.comment, files: data.files, status: data.status, approver: null, completedAt: null };
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, subtasks: [...p.subtasks, subtask] } : p));
    setSelectedProject({ ...selectedProject, subtasks: [...selectedProject.subtasks, subtask] });
    setNewSubtask({ title: '', comment: '', assignedTo: '', dueDate: '' });
    setSubtaskFiles([]);
    setShowSubtaskForm(false);
    if (newSubtask.assignedTo !== user.email) {
      createNotification('assigned', `${user.name} assigned you a task: "${newSubtask.title}"`, newSubtask.assignedTo);
    }
  };

  const updateSubtaskStatus = async (subtaskId, status) => {
    const completedAt = status === 'completed' ? new Date().toISOString() : null;
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, subtasks: p.subtasks.map(st => st.id === subtaskId ? { ...st, status, completedAt, approver: null } : st) } : p));
    setSelectedProject({ ...selectedProject, subtasks: selectedProject.subtasks.map(st => st.id === subtaskId ? { ...st, status, completedAt, approver: null } : st) });
    await supabase.from('latam_subtasks').update({ status, completed_at: completedAt, approver: null }).eq('id', subtaskId);
  };

  const sendForApproval = async () => {
    if (!selectedApprover) return;
    const subtask = approvalModal;
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, subtasks: p.subtasks.map(st => st.id === subtask.id ? { ...st, status: 'pending_approval', approver: selectedApprover } : st) } : p));
    setSelectedProject({ ...selectedProject, subtasks: selectedProject.subtasks.map(st => st.id === subtask.id ? { ...st, status: 'pending_approval', approver: selectedApprover } : st) });
    await supabase.from('latam_subtasks').update({ status: 'pending_approval', approver: selectedApprover }).eq('id', subtask.id);
    createNotification('approval_request', `${user.name} requests your approval for: "${subtask.title}"`, selectedApprover);
    setApprovalModal(null);
    setSelectedApprover('');
  };

  const deleteSubtask = async (subtaskId) => {
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, subtasks: p.subtasks.filter(st => st.id !== subtaskId) } : p));
    setSelectedProject({ ...selectedProject, subtasks: selectedProject.subtasks.filter(st => st.id !== subtaskId) });
    await supabase.from('latam_subtasks').delete().eq('id', subtaskId);
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    const { error } = await supabase.from('latam_comments').delete().eq('id', commentId);
    if (error) { showToast('Failed to delete comment', 'error'); return; }
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, comments: p.comments.filter(c => c.id !== commentId) } : p));
    setSelectedProject({ ...selectedProject, comments: selectedProject.comments.filter(c => c.id !== commentId) });
  };

  const startEditSubtask = (subtask) => { setEditingSubtask({ ...subtask }); };
  const saveEditSubtask = async () => {
    if (!editingSubtask.title || !editingSubtask.assignedTo || !editingSubtask.dueDate) return;
    const updatedSubtasks = selectedProject.subtasks.map(st => st.id === editingSubtask.id ? editingSubtask : st);
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, subtasks: updatedSubtasks } : p));
    setSelectedProject({ ...selectedProject, subtasks: updatedSubtasks });
    await supabase.from('latam_subtasks').update({ title: editingSubtask.title, assigned_to: editingSubtask.assignedTo, due_date: editingSubtask.dueDate, comment: editingSubtask.comment || '', status: editingSubtask.status }).eq('id', editingSubtask.id);
    setEditingSubtask(null);
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its tasks?')) return;
    setProjects(projects.filter(p => p.id !== projectId));
    setSelectedProject(null);
    await supabase.from('latam_projects').delete().eq('id', projectId);
  };

  const handleFileSelect = async (e) => {
    const { files, failed } = await uploadFiles('comments', Array.from(e.target.files));
    setCommentFiles(prev => [...prev, ...files]);
    if (failed) showToast(`${failed} file(s) failed to upload`, 'warning');
  };

  const handleSubtaskFileSelect = async (e) => {
    const { files, failed } = await uploadFiles('subtasks', Array.from(e.target.files));
    setSubtaskFiles(prev => [...prev, ...files]);
    if (failed) showToast(`${failed} file(s) failed to upload`, 'warning');
  };

  const removeSubtaskFile = (idx) => setSubtaskFiles(subtaskFiles.filter((_, i) => i !== idx));
  const removeFile = (idx) => setCommentFiles(commentFiles.filter((_, i) => i !== idx));

  const addComment = async () => {
    if (!newComment.trim() && commentFiles.length === 0) return;
    const mentions = [...new Set(commentMentions.filter(m => newComment.includes(`@${m.name}`)).map(m => m.email))];
    const { data, error } = await supabase.from('latam_comments').insert({
      project_id: selectedProject.id, text: stripTags(newComment), author: user.email, mentions, files: commentFiles
    }).select().single();
    if (error) { showToast('Failed to add comment'); return; }
    const comment = { id: data.id, text: data.text, author: data.author, createdAt: data.created_at, mentions: data.mentions, files: data.files };
    setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, comments: [...p.comments, comment] } : p));
    setSelectedProject({ ...selectedProject, comments: [...selectedProject.comments, comment] });
    mentions.forEach(email => {
      if (email !== user.email) createNotification('mention', `${user.name} mentioned you in ${selectedProject.name}`, email);
    });
    setNewComment('');
    setCommentMentions([]);
    setCommentFiles([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Regional execution</p>
          <h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">LATAM Projects</h2>
          <p className="text-sm text-slate-500 mt-1">Manage projects and team tasks</p>
        </div>
        <button onClick={() => setShowProjectModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
          <Plus className="w-5 h-5" /> New Project
        </button>
      </div>

      {/* Project Timeline Gantt */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-600" /> Project Timeline</h3>
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-500"></div><span>Overdue</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-orange-500"></div><span>Due Soon (≤7d)</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-500"></div><span>On Track</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500"></div><span>Completed</span></div>
        </div>
        {(() => {
          const projectsWithDeadlines = projects.map(p => {
            const pendingTasks = p.subtasks.filter(st => st.status !== 'completed' && st.dueDate);
            const completedTasks = p.subtasks.filter(st => st.status === 'completed');
            const allCompleted = p.subtasks.length > 0 && completedTasks.length === p.subtasks.length;
            const nextDueTask = pendingTasks.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];
            const lastDueTask = p.subtasks.filter(st => st.dueDate).sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))[0];
            return { ...p, nextDueDate: nextDueTask?.dueDate || lastDueTask?.dueDate, nextTask: nextDueTask?.title || 'All tasks completed', allCompleted };
          }).filter(p => p.nextDueDate).sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));
          if (projectsWithDeadlines.length === 0) return <p className="text-center text-slate-500 py-8">No projects with deadlines yet</p>;

          // --- Shared time axis (real Gantt) ---
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const starts = projectsWithDeadlines.map(p => new Date(p.createdAt || p.nextDueDate));
          const ends = projectsWithDeadlines.map(p => new Date(p.nextDueDate));
          let axisStart = new Date(Math.min(today.getTime(), ...starts.map(d => d.getTime())));
          let axisEnd = new Date(Math.max(today.getTime(), ...ends.map(d => d.getTime())));
          const pad = (axisEnd.getTime() - axisStart.getTime() || 1) * 0.06;
          axisStart = new Date(axisStart.getTime() - pad);
          axisEnd = new Date(axisEnd.getTime() + pad);
          const range = axisEnd.getTime() - axisStart.getTime() || 1;
          const pct = (d) => ((new Date(d).getTime() - axisStart.getTime()) / range) * 100;
          const todayPct = pct(today);

          // Month ticks within the visible range
          const ticks = [];
          const tick = new Date(axisStart.getFullYear(), axisStart.getMonth(), 1);
          if (tick < axisStart) tick.setMonth(tick.getMonth() + 1);
          while (tick <= axisEnd) {
            ticks.push({
              pct: pct(tick),
              label: tick.toLocaleDateString('en-US', { month: 'short' }) + (tick.getMonth() === 0 ? ` '${String(tick.getFullYear()).slice(2)}` : ''),
            });
            tick.setMonth(tick.getMonth() + 1);
          }

          return (
            <div className="relative">
              {/* Month axis header */}
              <div className="flex items-end gap-4 mb-3">
                <div className="w-56 shrink-0"></div>
                <div className="flex-1 relative h-4">
                  {ticks.map((t, i) => (
                    <span key={i} className="absolute -translate-x-1/2 text-[10px] font-medium text-slate-400 uppercase tracking-wide" style={{ left: `${t.pct}%` }}>{t.label}</span>
                  ))}
                  {todayPct >= 0 && todayPct <= 100 && (
                    <span className="absolute -translate-x-1/2 text-[10px] font-bold text-blue-600" style={{ left: `${todayPct}%` }}>Today</span>
                  )}
                </div>
                <div className="w-28 shrink-0"></div>
              </div>

              {/* Rows + gridline/today overlay */}
              <div className="relative">
                <div className="absolute top-0 bottom-0 left-[15rem] right-[8rem] pointer-events-none">
                  {ticks.map((t, i) => (
                    <div key={i} className="absolute top-0 bottom-0 w-px bg-slate-100" style={{ left: `${t.pct}%` }}></div>
                  ))}
                  {todayPct >= 0 && todayPct <= 100 && (
                    <div className="absolute top-0 bottom-0 w-0.5 bg-blue-500/60" style={{ left: `${todayPct}%` }}></div>
                  )}
                </div>

                <div className="space-y-2">
                  {projectsWithDeadlines.map((project, idx) => {
                    const days = Math.ceil((new Date(project.nextDueDate).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    const isOverdue = days < 0 && !project.allCompleted;
                    const isDueSoon = days >= 0 && days <= 7 && !project.allCompleted;
                    const barClass = project.allCompleted ? 'from-emerald-400 to-emerald-500' : isOverdue ? 'from-red-400 to-red-500' : isDueSoon ? 'from-orange-400 to-orange-500' : 'from-blue-400 to-blue-500';
                    const dotClass = project.allCompleted ? 'bg-emerald-500' : isOverdue ? 'bg-red-500' : isDueSoon ? 'bg-orange-500' : 'bg-blue-500';
                    const barLeft = Math.max(0, pct(project.createdAt || project.nextDueDate));
                    const barEnd = Math.min(100, pct(project.nextDueDate));
                    const barWidth = Math.max(barEnd - barLeft, 1.5);
                    return (
                      <div key={idx} className="flex items-center gap-4 group">
                        <div className="w-40 sm:w-56 shrink-0 min-w-0">
                          <div className="text-sm font-semibold text-slate-700 truncate" title={project.name}>{project.name}</div>
                          <div className="text-xs text-slate-400 truncate" title={project.nextTask}>Next: {project.nextTask}</div>
                        </div>
                        <div className="flex-1 relative h-8">
                          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-px bg-slate-100"></div>
                          <div className={`absolute top-1/2 -translate-y-1/2 h-3.5 rounded-full bg-gradient-to-r ${barClass} shadow-sm transition-all group-hover:h-4`} style={{ left: `${barLeft}%`, width: `${barWidth}%` }}></div>
                          <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full ring-2 ring-white shadow ${dotClass}`} style={{ left: `${barEnd}%` }}></div>
                        </div>
                        <div className="w-28 shrink-0 text-right">
                          <div className={`text-sm font-semibold ${project.allCompleted ? 'text-emerald-600' : isOverdue ? 'text-red-600' : isDueSoon ? 'text-orange-600' : 'text-slate-600'}`}>
                            {project.allCompleted ? '✓ Done' : isOverdue ? `${Math.abs(days)}d overdue` : `${days}d left`}
                          </div>
                          <div className="text-[11px] text-slate-400">{project.nextDueDate}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="grid gap-4">
        {projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No projects yet</h3>
            <p className="text-slate-500 mt-2">Create your first project to get started</p>
          </div>
        ) : (
          [...projects].sort((a, b) => {
            // Projects with pending tasks for me float to the top (most first);
            // sort is stable, so everything else keeps its original order.
            const mine = (p) => p.subtasks.filter(st => st.assignedTo === user.email && st.status !== 'completed').length;
            return mine(b) - mine(a);
          }).map(project => {
            const completed = project.subtasks.filter(st => st.status === 'completed').length;
            const total = project.subtasks.length;
            const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            const myPending = project.subtasks.filter(st => st.assignedTo === user.email && st.status !== 'completed').length;
            return (
              <div key={project.id} onClick={() => setSelectedProject(project)} className={`bg-white rounded-lg shadow-sm border p-6 hover:shadow-md cursor-pointer transition-shadow ${myPending > 0 ? 'border-l-4 border-l-blue-500 border-slate-200' : 'border-slate-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-800">{project.name}</h3>
                      {myPending > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          {myPending} task{myPending === 1 ? '' : 's'} for you
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{project.country} • {project.partner || 'No partner'}</p>
                    <p className="text-sm text-slate-600 mt-2">{project.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-800">{progress}%</div>
                    <p className="text-xs text-slate-500">{completed}/{total} tasks</p>
                  </div>
                </div>
                <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Modal isOpen={showProjectModal} onClose={() => setShowProjectModal(false)} title="Create New Project">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Project Name *</label>
            <input type="text" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Enter project name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" rows="3" placeholder="Brief description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <select value={newProject.country} onChange={e => setNewProject({ ...newProject, country: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Partner</label>
              <select value={newProject.partner} onChange={e => setNewProject({ ...newProject, partner: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                <option value="">Select partner...</option>
                {partners.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <button onClick={createProject} disabled={!newProject.name} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">Create Project</button>
        </div>
      </Modal>

      {selectedProject && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedProject(null)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-full sm:max-w-2xl bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">{selectedProject.name}</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => deleteProject(selectedProject.id)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium">Delete</button>
                  <button onClick={() => setSelectedProject(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-1">{selectedProject.country} • {selectedProject.partner || 'No partner'} • Created by {getUserName(selectedProject.createdBy)}</p>
            </div>

            <div className="p-4 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800">Tasks ({selectedProject.subtasks.length})</h3>
                  <button onClick={() => setShowSubtaskForm(true)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    <Plus className="w-4 h-4" /> Add Task
                  </button>
                </div>

                {showSubtaskForm && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <input type="text" value={newSubtask.title} onChange={e => setNewSubtask({ ...newSubtask, title: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Task title *" />
                    <textarea value={newSubtask.comment} onChange={e => setNewSubtask({ ...newSubtask, comment: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" rows="2" placeholder="Add details..." />
                    <div>
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                        <Paperclip className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-600">Attach files</span>
                        <input type="file" multiple onChange={handleSubtaskFileSelect} className="hidden" />
                      </label>
                      {subtaskFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {subtaskFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded text-xs">
                              <span className="truncate max-w-32">{file.name}</span>
                              <button onClick={() => removeSubtaskFile(idx)} className="text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <UserSelect value={newSubtask.assignedTo} onChange={v => setNewSubtask({ ...newSubtask, assignedTo: v })} label="Assign to *" />
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Due Date *</label>
                        <input type="date" value={newSubtask.dueDate} onChange={e => setNewSubtask({ ...newSubtask, dueDate: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={addSubtask} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Add Task</button>
                      <button onClick={() => { setShowSubtaskForm(false); setSubtaskFiles([]); }} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm">Cancel</button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {selectedProject.subtasks.map((st, idx) => (
                    <div key={st.id} id={`subtask-${st.id}`} className={`p-4 rounded-lg border-l-4 transition-shadow ${highlightSubtaskId === st.id ? 'ring-2 ring-blue-400 ring-offset-2' : ''} ${st.status === 'completed' ? 'bg-green-50 border-green-500' : st.status === 'pending_approval' ? 'bg-purple-50 border-purple-500' : st.status === 'in_progress' ? 'bg-blue-50 border-blue-500' : 'bg-slate-50 border-slate-300'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-slate-400">#{idx + 1}</span>
                            <StatusBadge status={st.status} />
                          </div>
                          <h4 className="font-semibold text-slate-800">{st.title}</h4>
                          {st.comment && <p className="text-sm text-slate-600 mt-1">{st.comment}</p>}
                          {st.files && st.files.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {st.files.map((file, fi) => (
                                <div key={fi} className="flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded text-xs hover:bg-slate-100">
                                  <Paperclip className="w-3 h-3" />
                                  <FileDownloadLink file={file} />
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-slate-500 mt-2">👤 {getUserName(st.assignedTo)} • 📅 {st.dueDate}{st.approver && <span> • Approval: {getUserName(st.approver)}</span>}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {st.status === 'pending' && <button onClick={() => updateSubtaskStatus(st.id, 'in_progress')} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Start</button>}
                          {st.status === 'in_progress' && (
                            <>
                              <button onClick={() => updateSubtaskStatus(st.id, 'completed')} className="px-2 py-1 bg-green-600 text-white text-xs rounded flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Complete</button>
                              <button onClick={() => setApprovalModal(st)} className="px-2 py-1 bg-purple-600 text-white text-xs rounded flex items-center gap-1"><Send className="w-3 h-3" /> Request</button>
                            </>
                          )}
                          <button onClick={() => deleteSubtask(st.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                          <button onClick={() => startEditSubtask(st)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Settings className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Comments ({selectedProject.comments.length})</h3>
                <div className="space-y-3 mb-4">
                  {selectedProject.comments.map(c => (
                    <div key={c.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-800 text-sm">{getUserName(c.author)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{formatDate(c.createdAt)}</span>
                          {(c.author === user.email || user.role === 'super_admin') && (
                            <button onClick={() => deleteComment(c.id)} title="Delete comment" className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {c.text && <p className="text-sm text-slate-700">{c.text.split(/(@\w+)/g).map((part, i) => part.startsWith('@') ? <span key={i} className="text-blue-600 font-semibold">{part}</span> : part)}</p>}
                      {c.files && c.files.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {c.files.map((file, fi) => (
                            <div key={fi} className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded text-sm hover:bg-slate-100">
                              <Paperclip className="w-4 h-4 text-slate-500" />
                              <FileDownloadLink file={file} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <MentionInput value={newComment} onChange={setNewComment} mentions={commentMentions} setMentions={setCommentMentions} excludeEmail={user.email} onSubmit={addComment} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Add comment... @mention to notify" />
                    <label className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg cursor-pointer hover:bg-slate-200">
                      <Paperclip className="w-5 h-5" />
                      <input type="file" multiple onChange={handleFileSelect} className="hidden" />
                    </label>
                    <button onClick={addComment} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Post</button>
                  </div>
                  {commentFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {commentFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                          <span className="truncate max-w-32">{file.name}</span>
                          <button onClick={() => removeFile(idx)} className="text-red-500"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={!!approvalModal} onClose={() => setApprovalModal(null)} title="Request Approval" size="sm">
        <div className="space-y-4">
          <p className="text-slate-600">Request approval for: <strong>{approvalModal?.title}</strong></p>
          <UserSelect value={selectedApprover} onChange={setSelectedApprover} exclude={[user.email]} label="Select Approver" />
          <button onClick={sendForApproval} disabled={!selectedApprover} className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">Send Request</button>
        </div>
      </Modal>

      <Modal isOpen={!!editingSubtask} onClose={() => setEditingSubtask(null)} title="Edit Task" size="md">
        {editingSubtask && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
              <input type="text" value={editingSubtask.title} onChange={e => setEditingSubtask({ ...editingSubtask, title: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Comment / Description</label>
              <textarea value={editingSubtask.comment || ''} onChange={e => setEditingSubtask({ ...editingSubtask, comment: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" rows="3" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To *</label>
                <select value={editingSubtask.assignedTo} onChange={e => setEditingSubtask({ ...editingSubtask, assignedTo: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                  <option value="">Select person...</option>
                  {profiles.filter(p => p.is_active).map(p => <option key={p.email} value={p.email}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date *</label>
                <input type="date" value={editingSubtask.dueDate} onChange={e => setEditingSubtask({ ...editingSubtask, dueDate: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select value={editingSubtask.status} onChange={e => setEditingSubtask({ ...editingSubtask, status: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <button onClick={saveEditSubtask} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Save Changes</button>
          </div>
        )}
      </Modal>
    </div>
  );
};
